import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, Signal, WritableSignal, inject, signal } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import {
  COOKIE_CATEGORIES,
  COOKIE_CONSENT_EXPIRY_DAYS,
  COOKIE_CONSENT_KEY,
  COOKIE_CONSENT_VERSION,
} from '../constants/cookie-consent.constants';
import { CookieCategory, ICookieConsent, ICookieConsentChangeEvent, ICookiePreferences } from '../interfaces/cookie-consent.interface';
import { LocalStorageService } from './local-storage.service';

/**
 * Service to manage cookie consent preferences following GDPR best practices.
 *
 * Key Features:
 * - Opt-in by default (no services enabled until explicit consent)
 * - Granular control per cookie category
 * - Persistent storage with encryption via LocalStorageService
 * - Consent expiry after 365 days
 * - Version tracking for policy changes
 * - Observable consent changes for reactive service initialization
 *
 * @example
 * ```typescript
 * // Check if analytics is enabled
 * if (this.cookieConsentService.hasConsent(CookieCategory.ANALYTICS)) {
 *   // Initialize analytics
 * }
 *
 * // Subscribe to consent changes
 * this.cookieConsentService.consentChanges$.subscribe(event => {
 *   if (event.category === CookieCategory.ANALYTICS && event.enabled) {
 *     // Initialize analytics service
 *   }
 * });
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class CookieConsentService {
  readonly #platformId = inject(PLATFORM_ID);
  readonly #localStorageService = inject(LocalStorageService);

  /** Signal indicating whether user has made a consent choice */
  public readonly hasUserConsented: Signal<boolean>;
  private readonly _hasUserConsented: WritableSignal<boolean> = signal(false);

  /** Signal for current consent preferences */
  public readonly preferences: Signal<ICookiePreferences>;
  private readonly _preferences: WritableSignal<ICookiePreferences>;

  /** Observable stream of consent changes */
  private readonly _consentChanges$ = new Subject<ICookieConsentChangeEvent>();
  public readonly consentChanges$: Observable<ICookieConsentChangeEvent> = this._consentChanges$.asObservable();

  /** Cookie categories metadata */
  public readonly categories = COOKIE_CATEGORIES;

  /** Computed signal for banner visibility */
  public readonly shouldShowBanner = signal(false);

  constructor() {
    // Initialize with default preferences (all false except necessary)
    this._preferences = signal(this.getDefaultPreferences());
    this.preferences = this._preferences.asReadonly();
    this.hasUserConsented = this._hasUserConsented.asReadonly();

    // Load existing consent from storage
    if (isPlatformBrowser(this.#platformId)) {
      this.loadConsent();
    }
  }

  /**
   * Get default cookie preferences (opt-in model)
   * Only necessary cookies are enabled by default
   */
  private getDefaultPreferences(): ICookiePreferences {
    return {
      [CookieCategory.NECESSARY]: true, // Always required
      [CookieCategory.ANALYTICS]: false,
    };
  }

  /**
   * Load consent preferences from localStorage
   */
  private async loadConsent(): Promise<void> {
    try {
      const stored = await this.#localStorageService.getData<ICookieConsent>(COOKIE_CONSENT_KEY);

      if (!stored) {
        this.shouldShowBanner.set(true);
        return; // No consent stored yet
      }

      // Check if consent is expired
      const expiryTime = stored.timestamp + COOKIE_CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
      if (Date.now() > expiryTime) {
        // Consent expired, clear it
        await this.clearConsent();
        this.shouldShowBanner.set(true);
        return;
      }

      // Check if consent version matches
      if (stored.version !== COOKIE_CONSENT_VERSION) {
        // Policy changed, re-prompt user
        await this.clearConsent();
        this.shouldShowBanner.set(true);
        return;
      }

      // Valid consent found
      this._preferences.set(stored.preferences);
      this._hasUserConsented.set(stored.hasConsented);
      this.shouldShowBanner.set(false);
    } catch (error) {
      console.error('[CookieConsent] Error loading consent:', error);
      this.shouldShowBanner.set(true);
    }
  }

  /**
   * Save consent preferences to localStorage
   */
  private async saveConsent(): Promise<void> {
    if (!isPlatformBrowser(this.#platformId)) {
      return;
    }

    const consent: ICookieConsent = {
      timestamp: Date.now(),
      version: COOKIE_CONSENT_VERSION,
      preferences: this._preferences(),
      hasConsented: true,
    };

    try {
      await this.#localStorageService.saveData(COOKIE_CONSENT_KEY, consent);
      this._hasUserConsented.set(true);
      this.shouldShowBanner.set(false);
    } catch (error) {
      console.error('[CookieConsent] Error saving consent:', error);
    }
  }

  /**
   * Clear stored consent (e.g., on expiry or version change)
   */
  private async clearConsent(): Promise<void> {
    if (!isPlatformBrowser(this.#platformId)) {
      return;
    }

    try {
      await this.#localStorageService.removeData(COOKIE_CONSENT_KEY);
      this._preferences.set(this.getDefaultPreferences());
      this._hasUserConsented.set(false);
    } catch (error) {
      console.error('[CookieConsent] Error clearing consent:', error);
    }
  }

  /**
   * Check if user has consented to a specific category
   */
  public hasConsent(category: CookieCategory): boolean {
    return this._preferences()[category] === true;
  }

  /**
   * Accept all cookie categories
   */
  public async acceptAll(): Promise<void> {
    const newPreferences: ICookiePreferences = {
      [CookieCategory.NECESSARY]: true,
      [CookieCategory.ANALYTICS]: true,
    };

    this._preferences.set(newPreferences);
    await this.saveConsent();

    // Emit consent changes for non-necessary categories
    this.emitConsentChange(CookieCategory.ANALYTICS, true);
  }

  /**
   * Decline all optional cookie categories (keep only necessary)
   */
  public async declineAll(): Promise<void> {
    const newPreferences = this.getDefaultPreferences();
    this._preferences.set(newPreferences);
    await this.saveConsent();

    // Emit consent changes for declined categories
    this.emitConsentChange(CookieCategory.ANALYTICS, false);
  }

  /**
   * Update specific category preference
   */
  public async updatePreference(category: CookieCategory, enabled: boolean): Promise<void> {
    // Cannot disable necessary cookies
    if (category === CookieCategory.NECESSARY) {
      return;
    }

    const currentPreferences = this._preferences();
    const newPreferences = {
      ...currentPreferences,
      [category]: enabled,
    };

    this._preferences.set(newPreferences);
    await this.saveConsent();

    // Emit consent change
    this.emitConsentChange(category, enabled);
  }

  /**
   * Save custom preferences (from settings modal)
   */
  public async saveCustomPreferences(preferences: ICookiePreferences): Promise<void> {
    // Ensure necessary cookies stay enabled
    preferences[CookieCategory.NECESSARY] = true;

    const oldPreferences = this._preferences();
    this._preferences.set(preferences);
    await this.saveConsent();

    // Emit changes for modified categories
    Object.entries(preferences).forEach(([category, enabled]) => {
      if (category !== CookieCategory.NECESSARY && oldPreferences[category as CookieCategory] !== enabled) {
        this.emitConsentChange(category as CookieCategory, enabled);
      }
    });
  }

  /**
   * Emit consent change event
   */
  private emitConsentChange(category: CookieCategory, enabled: boolean): void {
    this._consentChanges$.next({
      category,
      enabled,
      timestamp: Date.now(),
    });
  }

  /**
   * Get current preferences (for settings UI)
   */
  public getPreferences(): ICookiePreferences {
    return { ...this._preferences() };
  }

  /**
   * Reset all consent (for testing or user request)
   */
  public async resetConsent(): Promise<void> {
    await this.clearConsent();
    this.shouldShowBanner.set(true);
  }
}
